import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { structureApi, type PositionKind, type PositionNode } from "../../api/structure";
import type { ApiError } from "../../api/client";
import { setChildren } from "../../lib/positionTree";
import { TreeView } from "../../components/TreeView";
import { PositionKindIcon } from "../../components/PositionKindIcon";

/** The copied / saved position itself, drawn as a fixed row above its branch ("Sačuvaj kao šablon", 2026-10-20). */
export type PickerRoot = {
  name: string;
  kind: PositionKind;
  sequenceNo: number | null;
  /** Struck through: the position itself does not go along (its branch still may). */
  out: boolean;
  tag: string;
  extra?: ReactNode;
};

/**
 * The branch about to be copied, as a tree with a tick on every position
 * (2026-10-02: "da ne mora sve da povuče"). Clearing a tick leaves that
 * position out of the copy together with everything below it; `excluded` holds
 * only those top-most positions. The tree loads four levels and the rest on
 * expand — what is not loaded is simply copied, like before. "Sačuvaj kao
 * šablon" (2026-10-09) uses the same picker with its own words, and since
 * 2026-10-20 with the saved position as a row on top (`root`), the attribute
 * values next to every position (`renderExtra`) and the tree held back until
 * the caller's data is there too (`ready`), so nothing pops in afterwards.
 */
export function CopyExcludePicker({
  rootId, excluded, onChange, outTag, ariaLabel, root, renderExtra, ready = true,
}: {
  /** The position being copied; the tree shows what is below it. */
  rootId: string;
  excluded: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  /** The tag on a position that stays out; default "ne kopira se". */
  outTag?: string;
  /** The tree's accessible name; default "Pozicije koje se kopiraju". */
  ariaLabel?: string;
  root?: PickerRoot;
  /** Drawn after a position's name; `out` = the position does not go along. */
  renderExtra?: (node: PositionNode, out: boolean) => ReactNode;
  /** False while the caller still loads what `root` / `renderExtra` show. */
  ready?: boolean;
}) {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState<PositionNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const nodesRef = useRef<PositionNode[]>([]);

  const commit = useCallback((next: PositionNode[]) => {
    nodesRef.current = next;
    setNodes(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    structureApi
      .tree({ parentId: rootId, depth: 4 })
      .then((list) => { if (!cancelled) commit(list); })
      .catch((err: ApiError) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [rootId, commit]);

  const onExpand = useCallback(async (node: PositionNode) => {
    try {
      commit(setChildren(nodesRef.current, node.id, await structureApi.tree({ parentId: node.id, depth: 2 })));
    } catch (err) {
      setError((err as ApiError).message);
    }
  }, [commit]);

  // id → parent id for every loaded node; the direct children of the copied position map to null.
  const parentOf = useMemo(() => {
    const map = new Map<string, string | null>();
    const walk = (list: PositionNode[], parent: string | null) => {
      for (const n of list) {
        map.set(n.id, parent);
        if (n.children) walk(n.children, n.id);
      }
    };
    walk(nodes ?? [], null);
    return map;
  }, [nodes]);

  /** Whether a position goes out because one above it does. */
  const outByAncestor = useCallback((id: string) => {
    for (let p = parentOf.get(id) ?? null; p !== null; p = parentOf.get(p) ?? null) {
      if (excluded.has(p)) return true;
    }
    return false;
  }, [parentOf, excluded]);

  const toggle = useCallback((node: PositionNode) => {
    // A position under one that is left out cannot come back on its own.
    if (outByAncestor(node.id)) return;
    const next = new Set(excluded);
    if (next.has(node.id)) {
      next.delete(node.id);
    } else {
      // Leaving a position out covers what is below it; earlier picks down there are redundant.
      for (const id of excluded) {
        for (let p = parentOf.get(id) ?? null; p !== null; p = parentOf.get(p) ?? null) {
          if (p === node.id) { next.delete(id); break; }
        }
      }
      next.add(node.id);
    }
    onChange(next);
  }, [excluded, outByAncestor, parentOf, onChange]);

  const getId = useCallback((n: PositionNode) => n.id, []);
  const getChildren = useCallback((n: PositionNode) => n.children, []);
  const getHasChildren = useCallback((n: PositionNode) => n.hasChildren, []);
  const getLabel = useCallback((n: PositionNode) => {
    const own = excluded.has(n.id);
    const out = own || outByAncestor(n.id);
    return (
      <span className={`copy-node${out ? " is-out" : ""}`}>
        <input type="checkbox" checked={!out} readOnly tabIndex={-1} aria-hidden="true" />
        <span className="copy-node-name">
          {n.name}
          {n.sequenceNo !== null && <span className="muted"> #{n.sequenceNo}</span>}
        </span>
        {own && <span className="tag neutral">{outTag ?? t("structure.modal.copyOutTag", "ne kopira se")}</span>}
        {renderExtra?.(n, out)}
      </span>
    );
  }, [excluded, outByAncestor, t, outTag, renderExtra]);

  if (error) return <div className="empty" role="alert">{error}</div>;
  if (nodes === null || !ready) return <div className="empty">{t("common.loading", "Učitavanje…")}</div>;

  const tree = (
    <TreeView<PositionNode>
      nodes={nodes}
      getId={getId}
      getLabel={getLabel}
      getChildren={getChildren}
      getHasChildren={getHasChildren}
      onExpand={onExpand}
      selectedId={null}
      onSelect={toggle}
      renderIcon={(n) => <PositionKindIcon kind={n.kind} />}
      defaultExpandedDepth={2}
      ariaLabel={ariaLabel ?? t("structure.modal.copyPickAria", "Pozicije koje se kopiraju")}
      emptyText={t("structure.modal.copyNothingBelow", "Ova pozicija nema pod-pozicija.")}
      expandLabel={t("tree.expand", "Proširi")}
      collapseLabel={t("tree.collapse", "Skupi")}
      loadingLabel={t("common.loading", "Učitavanje…")}
    />
  );

  return (
    <div className={`tree-picker${root || renderExtra ? " is-rich" : ""}`}>
      {root ? (
        <>
          <div className="tree-row tree-picker-root">
            <span className="tree-toggle is-leaf" aria-hidden="true" />
            <span className="tree-icon"><PositionKindIcon kind={root.kind} /></span>
            <span className="tree-label">
              <span className={`copy-node${root.out ? " is-out" : ""}`}>
                <span className="copy-node-name">
                  {root.name}
                  {root.sequenceNo !== null && <span className="muted"> #{root.sequenceNo}</span>}
                </span>
                <span className="tag neutral">{root.tag}</span>
                {root.extra}
              </span>
            </span>
          </div>
          {nodes.length > 0 && <div className="tree-picker-below">{tree}</div>}
        </>
      ) : tree}
    </div>
  );
}
